import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import styled from 'styled-components';
import Recaptcha from 'react-recaptcha';

import { Textbox } from 'providerSite/components/inputs';
import { setIndividualAnswer } from '../utils';
import {
  FIELD_BUSINESS_NAME,
  FIELD_PHONE,
  FIELD_RECAPTCHA,
} from '../constants';
import RecaptchaProvider from 'components/RecaptchaProvider';
import config from 'config';
import {AuthAPI} from 'api';
import {toast} from 'react-hot-toast';
import SpinnerModal from 'components/SpinnerModal';


const COUNTRY_CODE = 'AU';


const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  justify-content: center;
  row-gap: 10px;
`;

const Row = styled.div`
  display: flex;
  justify-content: center;
  column-gap: 20px;
  width: 100%;
`;

const nameValidator = value => !!value?.match(/^[\w\-@&# ]+$/g);


const ProviderPage = ({ onChange, value }) => {

  const reCaptchaRef = useRef();
  const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneAvailable, setIsPhoneAvailable] = useState(null);
  const [isPhoneCurrentlyClicked, setIsPhoneCurrentlyClicked] = useState(false);
  const [data, setData] = useState(value ? value : {});

  useEffect(() => {
    onChange(data);
  }, [data]);

  const updateRecaptchaAnswer = code => setIndividualAnswer(setData, FIELD_RECAPTCHA, code);

  const isValidMobilePhoneNumber = number => {
    if (!!number && isValidPhoneNumber(number, COUNTRY_CODE)) {
      const phoneNumber = parsePhoneNumberFromString(number, COUNTRY_CODE);
      return phoneNumber.getType() === 'MOBILE';
    }
    return false;
  };

  const handlePhoneLookup = number => {
    if (!isValidMobilePhoneNumber(number)) return false;
    setIsLoading(true);
    AuthAPI.providerAvailable({phone: number})
      .then(res => res.payload)
      .then(res => {
        const isAvailable = !!res?.phone;
        setIsPhoneAvailable(isAvailable);
        setIndividualAnswer(setData, FIELD_PHONE, isAvailable ? number : null);
      })
      .catch(() => {
        toast.error('Sorry, something went wrong! Please contact the customer support.');
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <RecaptchaProvider>
      <Container>
        <Row>
          <Textbox
            title="What is your business name?"
            defaultValue={value?.[FIELD_BUSINESS_NAME]}
            onChange={val => setIndividualAnswer(setData, FIELD_BUSINESS_NAME, val)}
            validator={nameValidator}
            errorMsg="Please only use valid characters"
            colouredBorder
          />
        </Row>
        <Row>
          <Textbox
            title="What is your Australian mobile phone number?"
            subTitle="This mobile phone number is private and won’t be shown on your profile. It’s used for verification, security,
            and important account updates."
            defaultValue={value?.[FIELD_PHONE]}
            onChange={val => isValidMobilePhoneNumber(val) && !isPhoneCurrentlyClicked && handlePhoneLookup(val)}
            validator={val => isValidMobilePhoneNumber(val) && (isPhoneAvailable === null || isPhoneAvailable === true)}
            errorMsg={isPhoneAvailable === false ?
              <>
                An account already exists for that phone number,
                try <a href={`${config.website.URL}/providers/login`}>logging in</a> or&nbsp;
                <a href={`${config.website.URL}/providers/password/recover`}>forgot password</a>
              </>
              : 'Please enter a valid Australian mobile phone number'}
            placeholder="0450 123 456"
            onBlur={e => handlePhoneLookup(e.target.value)}
            onClick={() => setIsPhoneCurrentlyClicked(true)}
            colouredBorder
          />
        </Row>
        <Row>
          <div style={{ width: '100%' }}>
            <Recaptcha
              ref={reCaptchaRef}
              sitekey={config.settings.RECAPTCHA_KEY}
              render="explicit"
              onloadCallback={() => {
                updateRecaptchaAnswer(null);
                setIsRecaptchaLoading(false);
              }}
              verifyCallback={(code) => updateRecaptchaAnswer(code)}
              expiredCallback={() => updateRecaptchaAnswer(null)}
            />
          </div>
        </Row>
        <SpinnerModal isLoading={isLoading || isRecaptchaLoading} />
      </Container>
    </RecaptchaProvider>
  );
};

ProviderPage.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
};


export default ProviderPage;
